import {
  createContext,
  memo,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import { useLocation } from '@reach/router'
import flatten from 'lodash/flatten'
import max from 'lodash/max'
import minBy from 'lodash/minBy'

import {
  LOADING_ASSET_URL,
  imagePreloader,
  type DisplayAssetItem,
  AUDIO_CONTROL_ASSET_URLS,
} from 'lib/assetLoader'
import { useIsMobile } from 'hooks/responsive'
import { VariantAssetManifest } from 'lib/assetLoader/generateManifest'
import { desktopManifest, mobileManifest } from 'lib/assetLoader/manifests'
import { getSafeWindowDimensions } from 'hooks/useWindowDimensions'
import { isPathActive } from 'lib/urls'

interface AssetState {
  visualItems: VariantAssetManifest
  progress: number
}

export const AssetContext = createContext<AssetState>({
  visualItems: desktopManifest,
  progress: 0,
})

interface Props {
  children?: ReactNode
  displayAssetManifest: VariantAssetManifest
  pagePath: string
}

export const resolveSrc = (asset: DisplayAssetItem): string => {
  const { src, type } = asset

  if (type === 'image' && asset.srcSet) {
    const { innerWidth, devicePixelRatio } = getSafeWindowDimensions()

    const bestFit = minBy(
      asset.srcSet.filter((s) => innerWidth <= s.maxScreenWidth),
      (s) => s.maxScreenWidth
    )

    if (!bestFit) return src

    const srcDpr = max(
      Object.keys(bestFit.src).filter(
        (dpr) => parseInt(dpr) <= devicePixelRatio
      )
    )
    return srcDpr ? bestFit.src[parseInt(srcDpr)] : src
  }

  if (type === 'video') {
    return `${src}.webp`
  }

  return src
}

const preloadImagesForPath = (
  displayAssetManifest: VariantAssetManifest,
  pagePath: string
) => {
  const getMeetBottleAssets = () => {
    const assets = Object.values(displayAssetManifest).map((variantImages) =>
      resolveSrc(variantImages.productOpen)
    )
    assets.push(LOADING_ASSET_URL)
    assets.push(...AUDIO_CONTROL_ASSET_URLS(true))
    assets.push(...AUDIO_CONTROL_ASSET_URLS(false))

    return assets
  }

  const getBuyBottleAssets = () => {
    return flatten(
      Object.values(displayAssetManifest).map((variantImages) => [
        resolveSrc(variantImages.productClosed),
        resolveSrc(variantImages.productSide),
        resolveSrc(variantImages.productTop),
        resolveSrc(variantImages.productBottom),
      ])
    )
  }

  if (isPathActive('/', pagePath) || isPathActive('/meet/', pagePath)) {
    return getMeetBottleAssets()
  } else if (isPathActive('/bottle/', pagePath)) {
    return getBuyBottleAssets()
  }

  return []
}

const BaseAssetProvider: FC<Props> = memo(
  ({ children, displayAssetManifest, pagePath }) => {
    const [imageProgress, setImageProgress] = useState(0)

    useEffect(() => {
      let cancelled = false

      const displayAssetUrls = preloadImagesForPath(
        displayAssetManifest,
        pagePath
      )
      if (displayAssetUrls.length === 0 && !cancelled) {
        setImageProgress(100)
      }

      imagePreloader(
        displayAssetUrls,
        (imageProgress) => !cancelled && setImageProgress(imageProgress)
      ).then(() => {
        if (!cancelled) {
          setImageProgress(100)
        }
      })

      return () => {
        cancelled = true
      }
    }, [displayAssetManifest, pagePath])

    return (
      <AssetContext.Provider
        value={{
          visualItems: displayAssetManifest,
          progress: Math.min(100, imageProgress),
        }}
      >
        {children}
      </AssetContext.Provider>
    )
  }
)

const AssetProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const isMobile = useIsMobile()
  const location = useLocation()
  return (
    <BaseAssetProvider
      displayAssetManifest={isMobile ? mobileManifest : desktopManifest}
      pagePath={location.pathname}
      children={children}
    />
  )
}

export default AssetProvider
